/**
 * F07 — "the options I set don't apply to my next compare".
 *
 * Every route that starts a NEW comparison used to begin from a hardcoded
 * baseline, throwing away whatever the user had just dialled in:
 *   - `schemaCompareNavigation.ts` builds tab content with no `options` at
 *     all, so `createDefaultSchemaCompareContent()`'s all-true / Deep baseline
 *     wins through `workspaceStore.addTab`'s `{...default, ...content}` merge;
 *   - the toolbar / command-palette route renders `SchemaCompareSetupDialog`
 *     with no `initialOptions`, so the dialog's own state starts at `{}`.
 * Combined with `autoCompare: true` on the Blueprint route, a user who had
 * unticked Tables/Procedures/Functions saw the very next compare scan all of
 * them again — the reporter's "it is scanning the Tables again".
 *
 * ┌──────────────────────────────────────────────────────────────────────┐
 * │ SEAM: `resolveDefaultSchemaCompareOptions` is the ONE place a new    │
 * │ comparison reads its starting options from. W5.1 (saved per-engine   │
 * │ defaults, on `feat/soporte4-compare-options`) replaces this          │
 * │ function's body at the merge gate — every call site stays unchanged. │
 * └──────────────────────────────────────────────────────────────────────┘
 *
 * Keyed per engine on purpose: `ignoredProperties` is an engine-specific key
 * set (`ENGINE_PROPERTY_ALLOWLIST` in `utils/compareDepth.ts`), so replaying
 * an MSSQL selection (`fillFactor`, `identitySeed`, …) onto a PostgreSQL
 * compare would carry keys that engine never emits and make `inferDepth`
 * report a spurious "Custom" depth. Per-engine is also the shape D4 asked for
 * ("save as my defaults (per engine)"), so W5.1 slots straight in.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import {
  createDefaultSchemaCompareContent,
  SCHEMA_COMPARE_SCOPE_CAP_KEYS,
  type SchemaCompareOptions,
  type SchemaCompareScopeCapKey,
} from '../../core/types/workspaceTypes';
import { ENGINE_PROPERTY_ALLOWLIST } from '../utils/compareDepth';
import { getSchemaCompareDefaultOptions } from '../../../../store/appStore';
import { createGuardedJsonStorage } from '../../../../core/storage/guardedJsonStorage';

type SchemaCompareOptionDefaultsState = {
  /** Last options the user actually ran a comparison with, per engine. */
  lastUsedByEngine: Partial<Record<DatabaseEngineId, SchemaCompareOptions>>;
  remember: (engine: DatabaseEngineId, options: SchemaCompareOptions) => void;
  clear: () => void;
};

/**
 * Drop `ignoredProperties` keys the engine cannot emit.
 *
 * `ignoredProperties` is an engine-specific key set, so this is the invariant
 * the per-engine keying exists to guarantee. It is applied on the way IN
 * (`remember`, below) *and* on the way OUT (`resolveDefaultSchemaCompareOptions`),
 * because `persist` rehydrates whatever a pre-fix build already wrote to
 * localStorage without passing it through `remember` at all.
 */
function withEngineAllowedProperties(
  engine: DatabaseEngineId,
  options: SchemaCompareOptions,
): SchemaCompareOptions {
  const current = options.ignoredProperties;
  if (!current || current.length === 0) return { ...options };
  const allowed = new Set(ENGINE_PROPERTY_ALLOWLIST[engine]);
  return { ...options, ignoredProperties: current.filter((key) => allowed.has(key)) };
}

export const useSchemaCompareOptionDefaultsStore =
  create<SchemaCompareOptionDefaultsState>()(
    persist(
      (set) => ({
        lastUsedByEngine: {},

        // Fix-round-2 (finding C): the filter lives HERE, not in the free
        // function below. `remember` is public and is called directly (tests,
        // and anything W5.1 adds), so a filter one layer up would not be an
        // invariant — it would be a convention. Same reasoning the header
        // comment already gave; now the code actually does it.
        remember: (engine, options) => {
          set((state) => ({
            lastUsedByEngine: {
              ...state.lastUsedByEngine,
              [engine]: withEngineAllowedProperties(engine, options),
            },
          }));
        },

        clear: () => {
          set({ lastUsedByEngine: {} });
        },
      }),
      {
        name: 'sql-studio-schema-compare-option-defaults',
        // Crash-proof write (core/storage/guardedJsonStorage.ts): a full origin must not throw out
        // of set(). No toast here — these are remembered last-used options, never explicitly saved;
        // the drop is still reported to telemetry and the console.
        storage: createGuardedJsonStorage(),
        partialize: (state) => ({ lastUsedByEngine: state.lastUsedByEngine }),
      },
    ),
  );

/**
 * The hardcoded baseline a brand-new tab falls back to when the user has never
 * run a comparison on this engine. Derived from the tab-content factory rather
 * than restated, so the two can never drift.
 *
 * MERGE (this file's header SEAM note, honoured): W5.2 on
 * `feat/soporte4-compare-options` made the factory ENGINE-AWARE — with an
 * engine it returns that engine's **Standard** depth (its volatile properties
 * pre-ignored), not the engine-less all-in baseline. Threading `engine`
 * through is what makes a fresh dialog open on Standard instead of Deep;
 * without it this fallback silently re-imposed the pre-W5.2 depth on every
 * new comparison.
 */
export function factoryBaselineSchemaCompareOptions(
  engine?: DatabaseEngineId,
): SchemaCompareOptions {
  return createDefaultSchemaCompareContent(undefined, undefined, engine).options ?? {};
}

/**
 * Which of the options a dialog was HANDED are Database Blueprint scope caps?
 *
 * Prefers the recorded marker (`SchemaCompareTabContent.scopeCapKeys`, written
 * by the blueprint route and narrowed on every confirm). Falls back to the
 * endpoint-kind heuristic when `capKeys` is `undefined` — a tab that predates
 * the marker, i.e. one that has never yet been through `narrowScopeCapKeys`.
 *
 * Fix round 5 correction: that is NOT the same as "a tab restored from an
 * older build". `narrowScopeCapKeys` always returns a DEFINED array once it
 * has run once — even `[]` — so `undefined` only describes a tab's state
 * BEFORE its first confirm through this dialog. From the second confirm
 * onward `capKeys` is defined and this fallback is never consulted again for
 * that tab, whether or not the first confirm's caps were the ones the
 * endpoint-kind heuristic would have picked. See `narrowScopeCapKeys`'s own
 * comment for how its `fallback` parameter keeps that first confirm honest.
 *
 * Returns `undefined` when nothing was imposed, which is the ordinary case.
 */
export function pickInheritedCaps(
  handed: SchemaCompareOptions | undefined,
  capKeys: readonly SchemaCompareScopeCapKey[] | undefined,
  fallback: { folderEndpoint: boolean },
): SchemaCompareOptions | undefined {
  if (!handed) return undefined;
  if (capKeys) {
    if (capKeys.length === 0) return undefined;
    const picked: SchemaCompareOptions = {};
    for (const key of capKeys) picked[key] = handed[key];
    return picked;
  }
  return fallback.folderEndpoint ? { ...handed } : undefined;
}

/**
 * The recorded marker after a confirm: a cap the user just changed is their own
 * choice from now on and stops being a cap. Everything they left alone is still
 * the manifest's, and stays marked for the NEXT confirm — which is the whole
 * point, since by then the tab's endpoints no longer look like a folder
 * comparison at all.
 *
 * `fallback` mirrors `pickInheritedCaps`'s own parameter of the same name, and
 * closes a gap fix round 4 left open (found in the round-4 re-review): without
 * it, `capKeys ?? []` collapsed an UNDEFINED marker straight to `[]` on the
 * FIRST confirm of a folder-sourced tab that had never been through this
 * function before — a genuinely-imposed cap the endpoint-kind heuristic would
 * have caught at dialog-open time was then narrowed against the wrong
 * baseline (`[]`, not the real cap set), so anything the user did NOT touch
 * still survived by construction, but the marker itself came out `[]` if nothing
 * survived — indistinguishable from "no marker was ever imposed", so the
 * SECOND confirm no longer benefited from the endpoint-kind fallback either
 * (`pickInheritedCaps` reads `[]` as "nothing survives", not "ask the
 * heuristic"). Passing `fallback` here uses the same known cap-key set
 * (`SCHEMA_COMPARE_SCOPE_CAP_KEYS`) as the filter's starting point in that one
 * case, so the first confirm narrows against the real caps instead of against
 * nothing, and `pickInheritedCaps`'s fallback becomes true "for a tab that
 * predates the marker" by construction rather than by rounding.
 */
export function narrowScopeCapKeys(
  capKeys: readonly SchemaCompareScopeCapKey[] | undefined,
  handed: SchemaCompareOptions | undefined,
  chosen: SchemaCompareOptions,
  // W3.5 item 0 — REQUIRED, not optional. An optional 4th parameter let the
  // sole production call site's `fallback` argument be deleted and still
  // type-check and pass every existing test (see the incident this closes,
  // below); a required parameter whose TYPE still allows `undefined` keeps
  // every legitimate "no fallback" call working (write `undefined`
  // explicitly) while making silent omission a compile error instead of a
  // silent behaviour change.
  //
  // The incident: a folder-sourced session written by an earlier build (no
  // `scopeCapKeys` marker, narrowed options) would lose that history on the
  // first confirm after `fallback` was dropped — collapsing to `[]` instead
  // of the real surviving cap set — and the SECOND confirm would then persist
  // `includeViews:false, includeProcedures:false` as this engine's permanent
  // default. Every later unrelated MSSQL compare would silently omit views
  // and procedures and report "no differences", with zero red tests: none of
  // `schemaCompareOptionDefaultsStore.test.ts`'s 27, none of
  // `schemaCompareNavigation.test.ts` + `sessionStore.test.ts` +
  // `SchemaCompareTab.*.test.tsx`'s 174, and none of the three e2e fixtures in
  // `schemaCompareOptionsCarryOver.spec.ts` (all three carry a DEFINED
  // marker, so the fallback branch is never reached there) — because nothing
  // pinned the call site itself, only the function in isolation.
  fallback: { folderEndpoint: boolean } | undefined,
): SchemaCompareScopeCapKey[] {
  const effectiveCapKeys =
    capKeys ?? (fallback?.folderEndpoint ? SCHEMA_COMPARE_SCOPE_CAP_KEYS : []);
  return effectiveCapKeys.filter((key) => chosen[key] === handed?.[key]);
}

/**
 * Record the options a comparison was actually started with. Called from the
 * single choke point where the user's dialog choices become a real compare
 * (`SchemaCompareSetupDialog.handleOpenCompare`) — both for a new tab and for
 * "Change" on an existing one.
 *
 * No-ops when the engine is unknown: storing an unattributed options blob
 * would leak one engine's property keys into another engine's next compare,
 * which is exactly what the per-engine keying exists to prevent.
 *
 * `inheritedCaps` — the subset of the handed options a blueprint manifest
 * imposed (build it with `pickInheritedCaps`), otherwise omit it. A flag that
 * still equals what was handed in was never chosen by the user, so it must not
 * overwrite the engine's default; every flag the user did change, and every
 * option outside the imposed set, is remembered normally.
 *
 * Fix-round-2 (finding B): round 1 discarded the WHOLE options object for a
 * folder comparison, on the false premise that the object-type controls were
 * never rendered. By the time this runs both endpoints are live databases
 * (`canOpen` gates the button on `connectionId` + `database` for both sides),
 * so those controls were visible and editable for the entire dialog session,
 * and throwing the user's depth / ignored-property edits away was the very
 * complaint F07 exists to fix. Per-key is the discriminator that separates the
 * two cases.
 */
export function rememberSchemaCompareOptions(
  engine: DatabaseEngineId | undefined,
  options: SchemaCompareOptions,
  inheritedCaps?: SchemaCompareOptions,
): void {
  if (!engine) return;
  const next: SchemaCompareOptions = { ...options };

  if (inheritedCaps) {
    const stored = useSchemaCompareOptionDefaultsStore.getState().lastUsedByEngine[engine];
    for (const key of SCHEMA_COMPARE_SCOPE_CAP_KEYS) {
      if (!(key in inheritedCaps)) continue; // never imposed — an ordinary choice
      if (next[key] !== inheritedCaps[key]) continue; // the user changed it — keep it
      // Untouched cap: leave whatever this engine's default already said, and
      // say nothing at all if it had never said anything.
      const previous = stored?.[key];
      if (previous === undefined) delete next[key];
      else next[key] = previous;
    }
  }

  useSchemaCompareOptionDefaultsStore.getState().remember(engine, next);
}

/**
 * THE SEAM. Starting options for a NEW comparison.
 *
 * Precedence, highest first:
 *
 *  1. **The explicitly saved default** — "Save as my defaults" in the setup
 *     dialog, per engine, in `appStore.schemaCompareDefaultOptions`. It is
 *     already folded into `factoryBaselineSchemaCompareOptions` by
 *     `createDefaultSchemaCompareContent`, so returning the baseline IS
 *     returning the saved default over the hardcoded floor.
 *  2. **Last used** — written automatically by `rememberSchemaCompareOptions`
 *     on every Compare. A fallback only, for engines the user has never saved
 *     a default for.
 *  3. The hardcoded factory baseline.
 *
 * M2b — 1 and 2 used to be the other way round: `lastUsed` was returned
 * whenever it existed, which silently discarded the baseline and with it the
 * saved default. The consequence was that a SINGLE ad-hoc comparison
 * permanently overrode the button the user had pressed on purpose, and never
 * stopped overriding it — every subsequent compare rewrote `lastUsed` again.
 * "Save as my defaults" answers with *"New <engine> comparisons will start
 * with these settings"* (`SchemaCompareInlineOptions.handleSaveAsDefaults`),
 * so the old order made the control's own confirmation untrue. Explicit
 * outranks incidental; that is the only ordering under which a save button
 * means anything.
 *
 * Restoring a RECENT comparison is a different gesture and does not come
 * through here at all: `SchemaCompareSetupDialog.handleSelectRecent` applies
 * `entry.options` verbatim and latches `optionsSeededRef`, so a recent replays
 * exactly what that run used regardless of either default.
 *
 * Callers must only use this where the user has NOT already chosen options for
 * the comparison in question — i.e. a genuinely new tab/dialog. Applying it on
 * top of options the user just picked would overwrite them, which is the
 * inverse of the bug this fixes.
 */
export function resolveDefaultSchemaCompareOptions(
  engine: DatabaseEngineId | undefined,
): SchemaCompareOptions {
  if (!engine) return factoryBaselineSchemaCompareOptions();
  // Filtered on the way out in BOTH arms: `appStore` and this store are each
  // persisted, so a blob written by a build that predates the write-side
  // filter — or carried in by a settings-transfer bundle — rehydrates straight
  // out of localStorage without ever passing through `remember`.
  const baseline = withEngineAllowedProperties(
    engine,
    factoryBaselineSchemaCompareOptions(engine),
  );
  if (getSchemaCompareDefaultOptions(engine)) return baseline;
  const lastUsed = useSchemaCompareOptionDefaultsStore.getState().lastUsedByEngine[engine];
  return lastUsed ? withEngineAllowedProperties(engine, lastUsed) : baseline;
}
